// iter571 — Mirror of SupplierReconcileDialog for customers.
// One-click "Reconcile this customer" — shows total sales / cash / bank /
// outstanding (credit owed by customer) side-by-side with every sale +
// every payment, and offers a quick-fix Receive Payment action when the
// math doesn't tie.
//
// iter582 — Read-only by design (mirrors the iter580 supplier widget
// pattern). The only mutating action on this dialog is "Receive
// payment" which goes through the existing POST /sales/{id}/receive-credit
// endpoint with full validation and audit trail. No "auto-fix balance"
// button — fixes are user-driven, never automatic.
//
// iter582 — Outstanding formula now subtracts `total_discount_received`
// so settlement discounts (logged as {mode:"discount"} entries by
// /sales/{id}/receive-credit) don't produce phantom drift vs the credit
// balance. Same defensive pattern as iter578's supplier returns fix.

import { cur } from '@/lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Loader2, FileText, Banknote, AlertTriangle, Sparkles, CheckCircle2, HelpCircle } from 'lucide-react';
import api from '../../lib/api';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function CustomerReconcileDialog({ customer, onClose, onOpenReceive }) {
  const open = !!customer;
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (!open || !customer?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/customers/${customer.id}/report`).then((r) => r.data).catch(() => null),
      api.get(`/customers/${customer.id}/balance`).then((r) => r.data).catch(() => null),
    ]).then(([rep, bal]) => {
      setReport(rep);
      setBalance(bal);
    }).finally(() => setLoading(false));
  }, [open, customer?.id]);

  const totals = useMemo(() => {
    const total = Number(balance?.total_sales || report?.total || 0);
    const cash = Number(balance?.total_cash || 0);
    const bank = Number(balance?.total_bank || 0);
    // iter582 — Settlement discounts (mode:"discount" in payment_details)
    // count toward credit_received but were previously missing from the
    // Outstanding side, producing a one-sided phantom drift in the same
    // shape as the supplier-returns bug fixed in iter578. Now subtracted.
    const discountReceived = Number(balance?.total_discount_received || 0);
    const creditBalance = Number(balance?.credit_balance || 0); // credit_given − credit_received
    const outstanding = Math.max(0, total - cash - bank - discountReceived);
    return { total, cash, bank, discountReceived, creditBalance, outstanding };
  }, [balance, report]);

  const purchases = report?.purchases || [];
  const unpaid = purchases.filter((p) => (Number(p.credit) || 0) > 0);
  const matchHealth = Math.abs(totals.outstanding - totals.creditBalance) < 1;

  return (
    <TooltipProvider delayDuration={200}>
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[88dvh] overflow-y-auto" data-testid="customer-reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-600" /> Reconcile {customer?.name}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors ml-1"
                  data-testid="customer-reconcile-help"
                  aria-label="What does this dialog do?"
                >
                  <HelpCircle size={13} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-xs text-xs leading-relaxed">
                <p className="font-semibold mb-1">Read-only reconciliation snapshot</p>
                <p className="mb-1.5">
                  Compares this customer&apos;s <b>Outstanding</b> (Total Sales − Cash − Bank − Settlement Discounts) against their <b>Credit Balance</b> (Credit Given − Credit Received). A clean reconciliation means these two should match exactly.
                </p>
                <p className="text-amber-700 dark:text-amber-300">
                  No auto-fix runs from here — the only mutating action is <b>Receive payment</b>, which goes through the regular receipt flow with full audit trail.
                </p>
              </TooltipContent>
            </Tooltip>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
            <Loader2 size={20} className="animate-spin" />
            <div className="text-xs">Loading sales + receipts…</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats banner */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="customer-reconcile-stats">
              <div className="p-2 rounded-lg bg-muted/40 text-center">
                <div className="text-[10px] text-muted-foreground">Total Sales</div>
                <div className="text-sm font-bold">{fmt(totals.total)}</div>
              </div>
              <div className="p-2 rounded-lg bg-success/10 text-center">
                <div className="text-[10px] text-muted-foreground">Cash Received</div>
                <div className="text-sm font-bold text-success">{fmt(totals.cash)}</div>
              </div>
              <div className="p-2 rounded-lg bg-info/10 text-center">
                <div className="text-[10px] text-muted-foreground">Bank Received</div>
                <div className="text-sm font-bold text-info">{fmt(totals.bank)}</div>
              </div>
              <div className={`p-2 rounded-lg text-center ${totals.outstanding > 0 ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                <div className="text-[10px] text-muted-foreground">Outstanding</div>
                <div className={`text-sm font-bold ${totals.outstanding > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{fmt(totals.outstanding)}</div>
              </div>
              <div className={`p-2 rounded-lg text-center ${totals.creditBalance > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                <div className="text-[10px] text-muted-foreground">Credit Balance</div>
                <div className={`text-sm font-bold ${totals.creditBalance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmt(totals.creditBalance)}</div>
              </div>
            </div>
            {/* iter582 — Settlement-discount disclosure. Only renders when
                discounts have been applied during a credit settlement so
                the user can see why Outstanding doesn't equal Total - Paid
                straight up. */}
            {totals.discountReceived > 0 && (
              <div className="flex items-center justify-between text-[11px] px-2 py-1 rounded-md bg-stone-50 text-stone-700 border border-stone-200" data-testid="customer-reconcile-discount-row">
                <span>Settlement discounts applied (excluded from Outstanding)</span>
                <span className="font-mono font-semibold">−{fmt(totals.discountReceived)}</span>
              </div>
            )}

            {/* Health banner */}
            <div className={`flex items-center justify-between gap-2 p-2 rounded-lg text-xs ${matchHealth ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
              <span className="flex items-center gap-1.5 font-medium">
                {matchHealth ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                {matchHealth
                  ? 'Outstanding and Credit balance reconcile cleanly.'
                  : `Discrepancy: Outstanding ${fmt(totals.outstanding)} vs Credit ${fmt(totals.creditBalance)} (Δ ${fmt(Math.abs(totals.outstanding - totals.creditBalance))}). Likely an unbooked receipt or a sale paid without linking to a credit invoice.`}
              </span>
              {!matchHealth && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] border-amber-400 text-amber-900 hover:bg-amber-100 shrink-0"
                  onClick={() => onOpenReceive && onOpenReceive(customer)}
                  data-testid="customer-reconcile-quickfix"
                >
                  <Banknote size={12} className="mr-1" /> Receive payment
                </Button>
              )}
            </div>

            {/* Action bar */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                onClick={() => onOpenReceive && onOpenReceive(customer)}
                data-testid="customer-reconcile-action-receive"
              >
                <Banknote size={12} className="mr-1" /> Receive payment
              </Button>
            </div>

            {/* Two columns: Unpaid credit sales (left) + Recent activity (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="border rounded-xl overflow-hidden">
                <div className="px-3 py-2 bg-rose-50 border-b border-rose-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                    <AlertTriangle size={12} /> Unpaid credit sales
                  </span>
                  <Badge variant="secondary" className="font-normal">{unpaid.length}</Badge>
                </div>
                <div className="max-h-[260px] overflow-y-auto">
                  {unpaid.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">
                      <CheckCircle2 size={18} className="mx-auto mb-2 text-emerald-500" />
                      No unpaid credit sales — everything is settled.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Date</th>
                            <th className="text-left p-2 font-medium">Invoice</th>
                            <th className="text-right p-2 font-medium">Sale</th>
                            <th className="text-right p-2 font-medium">Credit due</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unpaid.slice(0, 100).map((p, i) => (
                            <tr key={i} className="border-t hover:bg-muted/30" data-testid={`customer-reconcile-unpaid-row-${i}`}>
                              <td className="p-2 whitespace-nowrap font-mono text-[10px]">{(p.date || '').slice(0, 10)}</td>
                              <td className="p-2 truncate max-w-[120px]" title={p.invoice_number}>{p.invoice_number || '—'}</td>
                              <td className="p-2 text-right font-mono">{fmt(p.amount)}</td>
                              <td className="p-2 text-right font-mono font-bold text-rose-700">{fmt(p.credit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="border rounded-xl overflow-hidden">
                <div className="px-3 py-2 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-stone-800 flex items-center gap-1.5">
                    <FileText size={12} /> Recent activity
                  </span>
                  <Badge variant="secondary" className="font-normal">{purchases.length}</Badge>
                </div>
                <div className="max-h-[260px] overflow-y-auto">
                  {purchases.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">No transactions yet.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Date</th>
                            <th className="text-left p-2 font-medium">Payment</th>
                            <th className="text-right p-2 font-medium">Sale</th>
                            <th className="text-right p-2 font-medium">Credit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchases.slice(0, 50).map((p, i) => (
                            <tr key={i} className="border-t hover:bg-muted/30">
                              <td className="p-2 whitespace-nowrap font-mono text-[10px]">{(p.date || '').slice(0, 10)}</td>
                              <td className="p-2 text-[10px] uppercase text-muted-foreground truncate max-w-[100px]" title={p.payment}>{p.payment || 'cash'}</td>
                              <td className="p-2 text-right font-mono">{fmt(p.amount)}</td>
                              <td className={`p-2 text-right font-mono ${Number(p.credit) > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                                {Number(p.credit) > 0 ? fmt(p.credit) : fmt(p.credit_received || 0).replace(`${cur()} `, '−SR ')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="customer-reconcile-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  );
}
